import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, formSecret, formText, TextField } from '../ui/index.js';

type MailboxList = Awaited<ReturnType<typeof api.get<'/api/v1/mailboxes'>>>;
type Mailbox = MailboxList['mailboxes'][number];
type TestResult = Awaited<ReturnType<typeof api.post<'/api/v1/mailboxes/test'>>>;

/**
 * Connecting a mailbox.
 *
 * The screen where somebody hands this product the password to their email, which makes it the
 * screen where being sloppy costs the most trust. Three things follow.
 *
 * **Test before connect, as a visible step.** The button says what it does and reports the two
 * failures separately, because "cannot reach that host" and "that password was refused" have
 * different fixes and one message for both sends everybody to the wrong one.
 *
 * **The password field is never re-populated.** Not from a response, not from state after a
 * success. There is nowhere for it to come back from, which is the point.
 *
 * **A connected mailbox says what happens next.** Connected is not active: nothing has been read
 * yet, and the backup has not been taken. Saying "done" here would be the first broken promise.
 */
/**
 * The outcomes the provider round trip can come back with.
 *
 * A closed set, because the value arrives in a query string that anybody can edit: an unrecognised
 * one is reported as a plain failure rather than looked up, so a crafted link cannot put chosen
 * text on the screen.
 */
const OUTCOMES: ReadonlySet<string> = new Set([
  'connected',
  'declined',
  'expired',
  'failed',
  'unconfigured',
  'no_refresh_token',
  'no_address',
]);

/** Statuses where somebody has to do something; everything else is the product working. */
const ATTENTION_STATUSES: ReadonlySet<Mailbox['status']> = new Set([
  'degraded',
  'reauth_required',
  'disconnected',
]);

/**
 * What we can work out from an address alone.
 *
 * The wizard's first question is the one a person always knows — their email address — and this
 * table is what lets the second question usually be just the password. Entries are settings the
 * providers document publicly; nothing here is guessed at runtime except the final fallback of
 * `imap.<domain>`, offered prefilled and editable rather than asserted.
 */
interface ProviderRoute {
  readonly kind: 'oauth-gmail' | 'oauth-microsoft' | 'imap';
  readonly imapHost?: string;
  readonly imapPort?: number;
  readonly imapSecurity?: 'tls' | 'starttls';
  /** A provider quirk worth saying before the password fails: app passwords, bridges. */
  readonly noteKey?: string;
}

const PROVIDER_ROUTES: Record<string, ProviderRoute> = {
  'gmail.com': { kind: 'oauth-gmail' },
  'googlemail.com': { kind: 'oauth-gmail' },
  'outlook.com': { kind: 'oauth-microsoft' },
  'hotmail.com': { kind: 'oauth-microsoft' },
  'live.com': { kind: 'oauth-microsoft' },
  'msn.com': { kind: 'oauth-microsoft' },
  'yahoo.com': {
    kind: 'imap',
    imapHost: 'imap.mail.yahoo.com',
    noteKey: 'wizard.note.appPassword',
  },
  'icloud.com': { kind: 'imap', imapHost: 'imap.mail.me.com', noteKey: 'wizard.note.appPassword' },
  'me.com': { kind: 'imap', imapHost: 'imap.mail.me.com', noteKey: 'wizard.note.appPassword' },
  'ziggo.nl': { kind: 'imap', imapHost: 'imap.ziggo.nl' },
  'kpnmail.nl': { kind: 'imap', imapHost: 'imap.kpnmail.nl' },
  'xs4all.nl': { kind: 'imap', imapHost: 'imap.xs4all.nl' },
  'gmx.com': { kind: 'imap', imapHost: 'imap.gmx.com' },
  'gmx.net': { kind: 'imap', imapHost: 'imap.gmx.net' },
  'web.de': { kind: 'imap', imapHost: 'imap.web.de' },
  'fastmail.com': {
    kind: 'imap',
    imapHost: 'imap.fastmail.com',
    noteKey: 'wizard.note.appPassword',
  },
  'protonmail.com': {
    kind: 'imap',
    imapHost: '127.0.0.1',
    imapPort: 1143,
    imapSecurity: 'starttls',
    noteKey: 'wizard.note.protonBridge',
  },
  'proton.me': {
    kind: 'imap',
    imapHost: '127.0.0.1',
    imapPort: 1143,
    imapSecurity: 'starttls',
    noteKey: 'wizard.note.protonBridge',
  },
};

function routeForAddress(address: string): ProviderRoute {
  const domain = address.slice(address.lastIndexOf('@') + 1).toLowerCase();
  return PROVIDER_ROUTES[domain] ?? { kind: 'imap', imapHost: `imap.${domain}` };
}

interface WizardPrefill {
  readonly emailAddress: string;
  readonly imapHost: string;
  readonly imapPort: number;
  readonly imapSecurity: 'tls' | 'starttls';
  readonly noteKey: string | null;
}

export function MailboxesScreen(): ReactNode {
  const { t } = useI18n();
  const { activeWorkspace } = useAuth();
  const navigate = useNavigate();

  const [list, setList] = useState<MailboxList | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState<TestResult | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Mailbox | null>(null);
  /*
   * The add-mailbox wizard.
   *
   * The first question is the one a person always knows — their address — and everything the
   * form needs beyond a password is usually derivable from it. `asking` shows the one-field
   * step; `route` holds what the address told us while the person picks between the
   * recommended path and IMAP; `prefill` seeds the full form, which stays the single place
   * credentials are typed and tested.
   */
  const [wizardAsking, setWizardAsking] = useState(false);
  const [wizardAddress, setWizardAddress] = useState('');
  const [wizardRoute, setWizardRoute] = useState<ProviderRoute | null>(null);
  const [prefill, setPrefill] = useState<WizardPrefill | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const securityId = useId();

  const tenantId = activeWorkspace?.id;
  const onboardingReturn = new URLSearchParams(window.location.search).get('onboarding') === '1';

  /*
   * What the provider round trip came back saying.
   *
   * Read from the address once, on mount, and then removed from it — a reload should not repeat
   * "connected", and a link somebody shares should not carry an outcome that was never theirs.
   */
  const [outcome, setOutcome] = useState<string | null>(null);

  useEffect(() => {
    const found = new URLSearchParams(window.location.search).get('oauth');
    if (found === null) return;
    setOutcome(found);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        setList(await api.get('/api/v1/mailboxes', undefined, { tenantId, signal }));
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        /* A failed load is shown as a failure, never as "no mailboxes" — which would invite
           somebody to connect a second copy of one they already have. */
        setList(null);
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const readForm = (form: HTMLFormElement) => {
    const data = new FormData(form);
    const value = (name: string): string => formText(data, name);
    const optional = (name: string): string | undefined => {
      const found = value(name);
      return found === '' ? undefined : found;
    };

    return {
      emailAddress: value('emailAddress'),
      ...(optional('displayName') === undefined ? {} : { displayName: optional('displayName')! }),
      imapHost: value('imapHost'),
      imapPort: Number(value('imapPort') || 993),
      imapSecurity: value('imapSecurity') === 'starttls' ? ('starttls' as const) : ('tls' as const),
      username: value('username') === '' ? value('emailAddress') : value('username'),
      /* Untrimmed: a trailing space is a character the mail server will check. */
      password: formSecret(data, 'password'),
      ...(optional('smtpHost') === undefined ? {} : { smtpHost: optional('smtpHost')! }),
    };
  };

  const readEditForm = (form: HTMLFormElement) => {
    const data = new FormData(form);
    const value = (name: string): string => formText(data, name);
    const optional = (name: string): string | undefined => {
      const found = value(name);
      return found === '' ? undefined : found;
    };

    return {
      ...(optional('displayName') === undefined
        ? { displayName: null }
        : { displayName: optional('displayName')! }),
      imapHost: value('imapHost'),
      imapPort: Number(value('imapPort') || 993),
      imapSecurity: value('imapSecurity') === 'starttls' ? ('starttls' as const) : ('tls' as const),
      username: value('username'),
      ...(value('password') === '' ? {} : { password: formSecret(data, 'password') }),
      ...(optional('smtpHost') === undefined ? {} : { smtpHost: optional('smtpHost')! }),
    };
  };

  const onTest = useCallback(async () => {
    const form = formRef.current;
    if (form === null || tenantId === undefined) return;
    /* Testing is a submit-shaped action on a form whose submit button means something else, so
       it borrows the browser's own validation rather than inventing a second set of rules. */
    if (!form.reportValidity()) return;
    setBusy(true);
    setTest(null);
    try {
      setTest(await api.post('/api/v1/mailboxes/test', readForm(form), undefined, { tenantId }));
      setFailure(null);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
    } finally {
      setBusy(false);
    }
  }, [tenantId]);

  const onConnect = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (tenantId === undefined) return;
      setBusy(true);
      try {
        await api.post('/api/v1/mailboxes', readForm(event.currentTarget), undefined, { tenantId });
        /* The form is discarded rather than cleared field by field, which is what guarantees the
           password does not survive in a controlled value somewhere. */
        setAdding(false);
        setTest(null);
        await load();
        if (onboardingReturn) window.location.assign('/welcome');
      } catch (error) {
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      } finally {
        setBusy(false);
      }
    },
    [tenantId, load, onboardingReturn],
  );

  const onUpdate = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (tenantId === undefined || editing === null) return;
      setBusy(true);
      try {
        await api.patch(
          '/api/v1/mailboxes/{mailboxId}',
          readEditForm(event.currentTarget),
          { mailboxId: editing.id },
          { tenantId },
        );
        setEditing(null);
        setFailure(null);
        await load();
      } catch (error) {
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      } finally {
        setBusy(false);
      }
    },
    [editing, tenantId, load],
  );

  /**
   * Hands the browser over to the provider.
   *
   * A full navigation rather than a popup. A popup is blocked as often as it is allowed, and a
   * consent screen in a window somebody cannot see the address bar of is the shape of every
   * phishing page — which is exactly the wrong thing to teach on the screen where the product asks
   * for access to their mail.
   */
  const startWizard = useCallback(() => {
    setEditing(null);
    setAdding(false);
    setTest(null);
    setWizardAddress('');
    setWizardRoute(null);
    setWizardAsking(true);
  }, []);

  const wizardContinue = useCallback(() => {
    const address = wizardAddress.trim();
    if (!address.includes('@')) return;
    const route = routeForAddress(address);
    if (route.kind === 'imap') {
      setPrefill({
        emailAddress: address,
        imapHost: route.imapHost ?? '',
        imapPort: route.imapPort ?? 993,
        imapSecurity: route.imapSecurity ?? 'tls',
        noteKey: route.noteKey ?? null,
      });
      setWizardAsking(false);
      setAdding(true);
    } else {
      /* The address says OAuth is the good path, but it stays a recommendation: a person with an
         app password and a reason gets IMAP one click away, not hidden. */
      setWizardRoute(route);
    }
  }, [wizardAddress]);

  const wizardImapAnyway = useCallback(() => {
    const address = wizardAddress.trim();
    const domain = address.slice(address.lastIndexOf('@') + 1).toLowerCase();
    setPrefill({
      emailAddress: address,
      imapHost: `imap.${domain}`,
      imapPort: 993,
      imapSecurity: 'tls',
      noteKey: 'wizard.note.appPassword',
    });
    setWizardRoute(null);
    setWizardAsking(false);
    setAdding(true);
  }, [wizardAddress]);

  const onOAuth = useCallback(
    async (provider: 'gmail' | 'microsoft') => {
      if (tenantId === undefined) return;
      setBusy(true);
      try {
        const started = await api.post('/api/v1/mailboxes/oauth/start', { provider }, undefined, {
          tenantId,
        });
        window.location.assign(started.authorizationUrl);
      } catch (error) {
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
        setBusy(false);
      }
    },
    [tenantId],
  );

  const onRemove = useCallback(
    async (mailbox: Mailbox) => {
      if (tenantId === undefined) return;
      if (!window.confirm(t('mailboxes.remove.confirm', { address: mailbox.emailAddress }))) return;
      setBusy(true);
      try {
        await api.delete('/api/v1/mailboxes/{mailboxId}', { mailboxId: mailbox.id }, { tenantId });
        setEditing(null);
        setFailure(null);
        await load();
      } catch (error) {
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      } finally {
        setBusy(false);
      }
    },
    [tenantId, load, t],
  );

  if (list === null && failure !== null) {
    return (
      <Alert tone="critical" title={t('mailboxes.error.title')}>
        <p>{t('mailboxes.error.body')}</p>
        <Button onClick={() => void load()}>{t('today.retry')}</Button>
      </Alert>
    );
  }

  if (list === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('mailboxes.title')}</h1>
        <p className="empty__body">{t('mailboxes.intro')}</p>
      </header>

      {outcome !== null && (
        <Alert
          tone={outcome === 'connected' ? 'success' : 'attention'}
          title={t(`mailboxes.oauth.${OUTCOMES.has(outcome) ? outcome : 'failed'}`)}
        >
          {t(`mailboxes.oauth.${OUTCOMES.has(outcome) ? outcome : 'failed'}.body`)}
        </Alert>
      )}

      {onboardingReturn && list.mailboxes.length > 0 && (
        <Alert tone="info">
          <Button onClick={() => window.location.assign('/welcome')}>
            {t('mailboxes.returnToOnboarding')}
          </Button>
        </Alert>
      )}

      {list.mailboxes.length === 0 && !adding && (
        <section className="card">
          <h2 className="card__title">{t('mailboxes.empty.title')}</h2>
          <p className="empty__body">{t('mailboxes.empty.body')}</p>
          <div className="mailbox-form__actions">
            <Button variant="primary" onClick={startWizard}>
              {t('mailboxes.add')}
            </Button>
            <Button disabled={busy} onClick={() => void onOAuth('gmail')}>
              {t('mailboxes.connectGmail')}
            </Button>
            <Button disabled={busy} onClick={() => void onOAuth('microsoft')}>
              {t('mailboxes.connectMicrosoft')}
            </Button>
          </div>
        </section>
      )}

      {list.mailboxes.length > 0 && (
        <section className="card">
          <h2 className="card__title">{t('mailboxes.connected')}</h2>
          <ul className="today-list">
            {list.mailboxes.map((mailbox) => (
              <li className="today-list__item" key={mailbox.id}>
                <span
                  className={`badge badge--${
                    mailbox.status === 'active'
                      ? 'ok'
                      : ATTENTION_STATUSES.has(mailbox.status)
                        ? 'critical'
                        : 'info'
                  }`}
                >
                  {t(`mailbox.status.${mailbox.status}`)}
                </span>
                <span className="today-list__subject">{mailbox.emailAddress}</span>
                <span className="today-list__meta">{mailbox.provider}</span>
                {mailbox.vaultVerified && (
                  <>
                    <span className="today-list__meta">{t('mailboxes.vaultVerified')}</span>
                    <Button variant="ghost" onClick={() => void navigate({ to: '/rescue' })}>
                      {t('mailboxes.openRescue')}
                    </Button>
                  </>
                )}
                {mailbox.connection !== null && (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      setAdding(false);
                      setEditing(mailbox);
                      setTest(null);
                    }}
                  >
                    {t('mailboxes.edit')}
                  </Button>
                )}
                <Button variant="ghost" disabled={busy} onClick={() => void onRemove(mailbox)}>
                  {t('mailboxes.remove')}
                </Button>
              </li>
            ))}
          </ul>
          {!adding && (
            <div className="mailbox-form__actions">
              <Button onClick={startWizard} disabled={busy}>
                {t('mailboxes.add')}
              </Button>
              <Button disabled={busy} onClick={() => void onOAuth('gmail')}>
                {t('mailboxes.connectGmail')}
              </Button>
              <Button disabled={busy} onClick={() => void onOAuth('microsoft')}>
                {t('mailboxes.connectMicrosoft')}
              </Button>
            </div>
          )}
        </section>
      )}

      {wizardAsking && (
        <section className="card">
          <h2 className="card__title">{t('wizard.title')}</h2>
          {wizardRoute === null ? (
            <>
              <p className="empty__body">{t('wizard.address.help')}</p>
              <form
                className="mailbox-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  wizardContinue();
                }}
              >
                <TextField
                  name="wizardAddress"
                  type="email"
                  label={t('mailboxes.field.email')}
                  required
                  value={wizardAddress}
                  onChange={(event) => setWizardAddress(event.currentTarget.value)}
                />
                <div className="mailbox-form__actions">
                  <Button variant="primary" type="submit">
                    {t('wizard.continue')}
                  </Button>
                  <Button type="button" onClick={() => setWizardAsking(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <>
              {/* The address named a provider whose good path is consent, not a password. */}
              <p className="empty__body">
                {t(
                  wizardRoute.kind === 'oauth-gmail'
                    ? 'wizard.route.gmail'
                    : 'wizard.route.microsoft',
                )}
              </p>
              <div className="mailbox-form__actions">
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() =>
                    void onOAuth(wizardRoute.kind === 'oauth-gmail' ? 'gmail' : 'microsoft')
                  }
                >
                  {t(
                    wizardRoute.kind === 'oauth-gmail'
                      ? 'mailboxes.connectGmail'
                      : 'mailboxes.connectMicrosoft',
                  )}
                </Button>
                <Button onClick={wizardImapAnyway}>{t('wizard.imapAnyway')}</Button>
                <Button onClick={() => setWizardRoute(null)}>{t('common.back')}</Button>
              </div>
            </>
          )}
        </section>
      )}

      {(adding || editing !== null) && (
        <section className="card">
          <h2 className="card__title">
            {t(editing === null ? 'mailboxes.form.title' : 'mailboxes.form.editTitle')}
          </h2>
          {adding && prefill?.noteKey != null && (
            <Alert tone="info" title={t(prefill.noteKey)}>
              {t(`${prefill.noteKey}.body`)}
            </Alert>
          )}
          {/*
            One form, two submit buttons. Testing and connecting take exactly the same fields, and
            a separate test dialog would mean typing a password into two places.
          */}
          <form
            className="mailbox-form"
            ref={formRef}
            onSubmit={(event) => void (editing === null ? onConnect(event) : onUpdate(event))}
          >
            {editing === null ? (
              <TextField
                name="emailAddress"
                type="email"
                label={t('mailboxes.field.email')}
                defaultValue={prefill?.emailAddress ?? ''}
                required
              />
            ) : (
              <p className="today-list__subject">{editing.emailAddress}</p>
            )}
            <TextField
              name="displayName"
              label={t('mailboxes.field.displayName')}
              defaultValue={editing?.displayName ?? ''}
            />
            <TextField
              name="imapHost"
              label={t('mailboxes.field.imapHost')}
              defaultValue={editing?.connection?.imapHost ?? prefill?.imapHost ?? ''}
              required
            />
            <TextField
              name="imapPort"
              type="number"
              label={t('mailboxes.field.imapPort')}
              defaultValue={String(editing?.connection?.imapPort ?? prefill?.imapPort ?? 993)}
            />
            <div className="field-group">
              <label className="field-group__label" htmlFor={securityId}>
                {t('mailboxes.field.security')}
              </label>
              <select
                className="select"
                id={securityId}
                name="imapSecurity"
                defaultValue={editing?.connection?.imapSecurity ?? prefill?.imapSecurity ?? 'tls'}
              >
                <option value="tls">{t('mailboxes.security.tls')}</option>
                <option value="starttls">{t('mailboxes.security.starttls')}</option>
              </select>
            </div>
            <TextField
              name="username"
              label={t('mailboxes.field.username')}
              defaultValue={editing?.connection?.username ?? prefill?.emailAddress ?? ''}
              required={editing !== null}
            />
            {/*
              `new-password` rather than `current-password`, so a browser does not offer the
              credentials for *this* site when somebody is typing the ones for their mail provider.
            */}
            <TextField
              name="password"
              type="password"
              label={t('mailboxes.field.password')}
              autoComplete="new-password"
              required={editing === null}
            />
            {editing !== null && (
              <p className="today-list__meta">{t('mailboxes.field.passwordHelp')}</p>
            )}
            <TextField
              name="smtpHost"
              label={t('mailboxes.field.smtpHost')}
              defaultValue={editing?.connection?.smtpHost ?? ''}
            />

            {test !== null && (
              <Alert
                tone={test.authenticated ? 'success' : 'critical'}
                title={t(
                  test.authenticated
                    ? 'mailboxes.test.ok'
                    : test.reachable
                      ? 'mailboxes.test.authFailed'
                      : 'mailboxes.test.unreachable',
                )}
              >
                {/* The two failures are named separately, because the fixes are different. */}
                <p>
                  {t(
                    `mailboxes.test.hint.${test.authenticated ? 'ok' : test.reachable ? 'auth' : 'host'}`,
                  )}
                </p>
              </Alert>
            )}

            <div className="mailbox-form__actions">
              <Button type="submit" variant="primary" disabled={busy} loading={busy}>
                {t(editing === null ? 'mailboxes.connect' : 'mailboxes.save')}
              </Button>
              {editing === null && (
                <Button type="button" disabled={busy} onClick={() => void onTest()}>
                  {t('mailboxes.test')}
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setAdding(false);
                  setEditing(null);
                  setTest(null);
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </section>
      )}

      {failure !== null && (
        /* The code is shown verbatim next to the plain-language line rather than mapped through a
           catalogue of every code the API can return: a stale map renders a wrong explanation,
           and a wrong explanation about a rejected password is worse than an unfamiliar word. */
        <Alert tone="critical" title={t('mailboxes.error.title')}>
          <p>{t('mailboxes.error.body')}</p>
          <p className="today-list__meta">{failure}</p>
        </Alert>
      )}
    </>
  );
}
